import {FC, useCallback, useMemo, useState} from 'react';
import SunEditor from 'suneditor-react';
import SetOptions from 'suneditor-react/dist/types/SetOptions';
import {IRichtextProps} from './IRichtextProps';
import plugins from 'suneditor/src/plugins';
import {TextField} from '@fluentui/react';
import {ModalWithHeader} from '../ModalWithHeader';
import {TheButton} from '../TheButton';
import styles from './Richtext.module.scss';
import './floatingTooltip';


interface SunEditorPlugin {
  name: string;
  display: string;
  title: string;
  buttonClass: string;
  innerHTML: string;
  add: (core: any, targetElement: HTMLElement) => void;
  action: (this: any) => void;
}

interface ITooltipInsertionContext {
  core: any;
  range: Range;
  selectedText: string;
  tooltipElement?: HTMLElement | null;
  initialTooltipValue?: string;
}

interface IDetailsInsertionContext {
  core: any;
  range: Range;
  selectedText: string;
  selectedHtml: string;
}

const getClosestTooltipElement = (node: Node | null): HTMLElement | null => {
  if (!node) return null;

  if (node instanceof HTMLElement) {
    return node.closest('.se-tooltip');
  }

  return node.parentElement?.closest('.se-tooltip') || null;
};

const getEditorContents = (core: any): string => {
  if (typeof core?.getContents === 'function') {
    const contents = core.getContents();
    if (typeof contents === 'string') {
      return contents;
    }
  }

  return core?.context?.element?.wysiwyg?.innerHTML || '';
};

const isHtmlContentEmpty = (value: string): boolean => {
  const rawValue = value || '';

  if (!rawValue.trim()) {
    return true;
  }

  if (typeof document === 'undefined') {
    return rawValue.replace(/<[^>]*>/g, '').trim().length === 0;
  }

  const container = document.createElement('div');
  container.innerHTML = rawValue;

  const normalizedText = (container.textContent || '')
    .replace(/\u00A0/g, ' ')
    .replace(/\u200B/g, '')
    .trim();

  return normalizedText.length === 0;
};

const createTooltipPlugin = (
  onMissingSelection: () => void,
  onTooltipRequested: (context: ITooltipInsertionContext) => void
): SunEditorPlugin => ({
  name: 'tooltip',
  display: 'command',
  title: 'Add Tooltip',
  buttonClass: '',
  innerHTML: '💬',
  add(core, targetElement) {
    core.context.tooltip = {
      button: targetElement
    };
  },
  action() {
    const core = this;
    const range = core.getRange();

    const tooltipElement =
      getClosestTooltipElement(range?.startContainer || null) ||
      getClosestTooltipElement(range?.endContainer || null) ||
      getClosestTooltipElement(range?.commonAncestorContainer || null);

    if (tooltipElement) {
      onTooltipRequested({
        core,
        range: range.cloneRange(),
        selectedText: tooltipElement.textContent || range.toString(),
        tooltipElement,
        initialTooltipValue: tooltipElement.getAttribute('data-tip') || ''
      });
      return;
    }

    if (!range || range.collapsed) {
      onMissingSelection();
      return;
    }

    onTooltipRequested({
      core,
      range: range.cloneRange(),
      selectedText: range.toString()
    });
  }
});

const createDetailsSummaryPlugin = (
  onMissingSelection: () => void,
  onDetailsSummaryRequested: (context: IDetailsInsertionContext) => void
): SunEditorPlugin => ({
  name: 'detailsSummary',
  display: 'command',
  title: 'Add Details',
  buttonClass: '',
  innerHTML: '📄',
  add(core, targetElement) {
    core.context.detailsSummary = {
      button: targetElement
    };
  },
  action() {
    const core = this;
    const range = core.getRange();

    if (!range || range.collapsed) {
      onMissingSelection();
      return;
    }

    const container = document.createElement('div');
    container.appendChild(range.cloneContents());

    onDetailsSummaryRequested({
      core,
      range: range.cloneRange(),
      selectedText: range.toString(),
      selectedHtml: container.innerHTML
    });
  }
});

export const Richtext: FC<IRichtextProps> = ({
  onChange,
  defaultValue,
  value,
  readOnly,
  disable,
  hideToolbar
}) => {
  const [isSelectionInfoVisible, setIsSelectionInfoVisible] = useState(false);
  const [isTooltipModalVisible, setIsTooltipModalVisible] = useState(false);
  const [tooltipValue, setTooltipValue] = useState('');
  const [pendingTooltipContext, setPendingTooltipContext] =
    useState<ITooltipInsertionContext | null>(null);
  const [isDetailsSummaryModalVisible, setIsDetailsSummaryModalVisible] = useState(false);
  const [detailsSummaryValue, setDetailsSummaryValue] = useState('');
  const [pendingDetailsSummaryContext, setPendingDetailsSummaryContext] =
    useState<IDetailsInsertionContext | null>(null);

  const onMissingSelection = useCallback(() => {
    setIsSelectionInfoVisible(true);
  }, []);

  const onTooltipRequested = useCallback((context: ITooltipInsertionContext) => {
    setPendingTooltipContext(context);
    setTooltipValue(context.initialTooltipValue || '');
    setIsTooltipModalVisible(true);
  }, []);

  const onDetailsSummaryRequested = useCallback((context: IDetailsInsertionContext) => {
    setPendingDetailsSummaryContext(context);
    setDetailsSummaryValue('');
    setIsDetailsSummaryModalVisible(true);
  }, []);

  const tooltipPlugin = useMemo(
    () => createTooltipPlugin(onMissingSelection, onTooltipRequested),
    [onMissingSelection, onTooltipRequested]
  );

  const detailsSummaryPlugin = useMemo(
    () => createDetailsSummaryPlugin(onMissingSelection, onDetailsSummaryRequested),
    [onMissingSelection, onDetailsSummaryRequested]
  );

  const closeTooltipModal = useCallback(() => {
    setIsTooltipModalVisible(false);
    setTooltipValue('');
    setPendingTooltipContext(null);
  }, []);

  const closeDetailsSummaryModal = useCallback(() => {
    setIsDetailsSummaryModalVisible(false);
    setDetailsSummaryValue('');
    setPendingDetailsSummaryContext(null);
  }, []);

  const syncEditorContent = useCallback(
    (core: any) => {
      onChange(getEditorContents(core));
    },
    [onChange]
  );

  const normalizedTooltipValue = tooltipValue.trim();
  const isTooltipValueEmpty = isHtmlContentEmpty(normalizedTooltipValue);

  const applyTooltip = useCallback(() => {
    const tip = tooltipValue.trim();
    const isTipEmpty = isHtmlContentEmpty(tip);
    if (!pendingTooltipContext) return;

    const {core, range, selectedText, tooltipElement} = pendingTooltipContext;

    if (tooltipElement) {
      if (isTipEmpty) {
        const parent = tooltipElement.parentNode;
        const nextSibling = tooltipElement.nextSibling;

        while (tooltipElement.firstChild) {
          parent?.insertBefore(tooltipElement.firstChild, tooltipElement);
        }
        parent?.removeChild(tooltipElement);

        if (nextSibling) {
          core.setRange(nextSibling, 0, nextSibling, 0);
        }
      } else {
        tooltipElement.setAttribute('data-tip', tip);
      }

      syncEditorContent(core);

      closeTooltipModal();
      return;
    }

    if (isTipEmpty) return;

    core.setRange(range.startContainer, range.startOffset, range.endContainer, range.endOffset);

    const span = core.util.createElement('SPAN');
    span.className = 'se-tooltip';
    span.setAttribute('data-tip', tip);
    span.textContent = selectedText;

    core.removeNode();
    core.insertNode(span);

    syncEditorContent(core);

    if (span.nextSibling) {
      core.setRange(span.nextSibling, 0, span.nextSibling, 0);
    }

    closeTooltipModal();
  }, [tooltipValue, pendingTooltipContext, syncEditorContent, closeTooltipModal]);

  const applyDetailsSummary = useCallback(() => {
    const summary = detailsSummaryValue.trim();
    if (!pendingDetailsSummaryContext || !summary) return;

    const {core, range, selectedHtml, selectedText} = pendingDetailsSummaryContext;
    const wysiwyg: HTMLElement = core.context.element.wysiwyg;

    // Escape summary for safe HTML insertion
    const escapedSummary = summary
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

    const contentHtml = selectedHtml.trim() || selectedText;
    const detailsHtml = `<details><summary><span style="font-size: 24px;font-weight: bold;">${escapedSummary}</span></summary>${contentHtml}</details><hr>`;

    core.setRange(range.startContainer, range.startOffset, range.endContainer, range.endOffset);
    wysiwyg.focus();

    document.execCommand('insertHTML', false, detailsHtml);

    syncEditorContent(core);
    closeDetailsSummaryModal();
  }, [
    detailsSummaryValue,
    pendingDetailsSummaryContext,
    syncEditorContent,
    closeDetailsSummaryModal
  ]);

  const suneditorSettings: SetOptions = useMemo(
    () => ({
      mode: 'classic',
      rtl: false,
      katex: 'window.katex',
      imageFileInput: false,
      videoFileInput: false,
      tabDisable: false,
      defaultStyle: `font-size: 16px; font-family: 'ABBvoice';`,
      font: ['ABBvoice'],
      plugins: [...Object.values(plugins), tooltipPlugin as any, detailsSummaryPlugin as any],
      buttonList: [
        [
          'undo',
          'redo',
          'font',
          'fontSize',
          'formatBlock',
          'paragraphStyle',
          'blockquote',
          'bold',
          'underline',
          'italic',
          'strike',
          'subscript',
          'superscript',
          'fontColor',
          'hiliteColor',
          'textStyle',
          'removeFormat',
          'outdent',
          'indent',
          'align',
          'horizontalRule',
          'list',
          'lineHeight',
          'table',
          'link',
          'tooltip',
          //'detailsSummary',
          'image',
          'video',
          'audio',
          'math',
          'fullScreen',
          'showBlocks',
          'codeView',
          'preview',
          'print'
        ]
      ],
      'lang(In nodejs)': 'en'
    }),
    [tooltipPlugin, detailsSummaryPlugin]
  );

  return (
    <>
      <SunEditor
        height={'100px'}
        defaultValue={defaultValue}
        setContents={value}
        readOnly={readOnly}
        disable={disable}
        hideToolbar={hideToolbar}
        setOptions={suneditorSettings}
        onChange={onChange}
      />
      <ModalWithHeader
        isVisible={isSelectionInfoVisible}
        dismiss={() => setIsSelectionInfoVisible(false)}
        header="Description Editor"
        isBlocking>
        <p>Please select some text.</p>
      </ModalWithHeader>
      <ModalWithHeader
        isVisible={isTooltipModalVisible}
        dismiss={closeTooltipModal}
        header={pendingTooltipContext?.tooltipElement ? 'Edit Tooltip' : 'Add Tooltip'}
        isBlocking>
        <Richtext
          value={tooltipValue}
          onChange={content => {
            setTooltipValue(content || '');
          }}
        />
        <div className={styles.buttonContainer}>
          <TheButton onClick={closeTooltipModal}>Cancel</TheButton>
          <TheButton
            primary
            onClick={applyTooltip}
            disabled={isTooltipValueEmpty && !pendingTooltipContext?.tooltipElement}>
            {isTooltipValueEmpty && pendingTooltipContext?.tooltipElement
              ? 'Remove tooltip'
              : pendingTooltipContext?.tooltipElement
              ? 'Save'
              : 'Add'}
          </TheButton>
        </div>
      </ModalWithHeader>
      <ModalWithHeader
        isVisible={isDetailsSummaryModalVisible}
        dismiss={closeDetailsSummaryModal}
        header="Add Details"
        isBlocking>
        <TextField
          label="Summary title"
          value={detailsSummaryValue}
          onChange={(event, nextValue) => setDetailsSummaryValue(nextValue || '')}
        />
        <div className={styles.buttonContainer}>
          <TheButton onClick={closeDetailsSummaryModal}>Cancel</TheButton>
          <TheButton primary onClick={applyDetailsSummary} disabled={!detailsSummaryValue.trim()}>
            Add
          </TheButton>
        </div>
      </ModalWithHeader>
    </>
  );
};
